import { useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { analytics } from "@/hooks/useAnalytics";
import { formatAuthError, logAuthError } from "@/utils/authErrorHandler";

export function useAuthMethods(setIsLoading: (value: boolean) => void) {
  const { toast } = useToast();

  // Use useRef to track the visibility change handler within hook scope
  // This prevents HMR leakage that occurs with module-level variables
  const visibilityChangeHandlerRef = useRef<(() => void) | null>(null);

  // Cleanup function to remove existing handler
  const cleanupVisibilityHandler = useCallback(() => {
    if (visibilityChangeHandlerRef.current) {
      document.removeEventListener(
        "visibilitychange",
        visibilityChangeHandlerRef.current,
      );
      visibilityChangeHandlerRef.current = null;
    }
  }, []);

  const signIn = async (email: string, password: string, rememberMe = true) => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Log detailed error information for debugging
        logAuthError(error, "Sign In");

        // Show user-friendly error message based on specific error code
        const errorInfo = formatAuthError(error);
        toast({
          variant: errorInfo.variant,
          title: errorInfo.title,
          description: errorInfo.description,
        });
        return { error, redirectTo: null };
      }

      // Handle remember me functionality after successful login
      if (!rememberMe && data.session) {
        // For non-persistent sessions, set an expiration marker
        sessionStorage.setItem("auth-temporary-session", "true");

        // Remove any existing visibility change handler first to prevent duplicates
        cleanupVisibilityHandler();

        // Create and store the new visibility change handler
        // This is more reliable than beforeunload for async operations
        visibilityChangeHandlerRef.current = () => {
          if (
            document.visibilityState === "hidden" &&
            sessionStorage.getItem("auth-temporary-session")
          ) {
            // Clean up immediately to prevent multiple calls
            sessionStorage.removeItem("auth-temporary-session");

            // Async signOut has more time to complete with visibilitychange
            supabase.auth.signOut().catch((error) => {
              console.error("Error during automatic sign out:", error);
            });
          }
        };
        document.addEventListener(
          "visibilitychange",
          visibilityChangeHandlerRef.current,
        );
      } else {
        // For persistent sessions, clean up any existing handler
        cleanupVisibilityHandler();
        sessionStorage.removeItem("auth-temporary-session");
      }

      // Track sign in event in Segment
      if (data.user) {
        analytics.trackSignIn(data.user.id, {
          email: data.user.email,
          auth_provider: "email",
        });
      }

      toast({
        variant: "success",
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });

      // Don't return a hardcoded redirect - let the auth state change handler decide
      return { error: null, redirectTo: null };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Authentication failed",
        description: "An unexpected error occurred. Please try again.",
      });
      return { error: error as Error, redirectTo: null };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    organizationName?: string,
    fbclid?: string | null,
  ) => {
    console.group(`[Auth Debug] Sign Up Attempt`);
    console.log("Email:", email);
    console.log("Has password:", !!password);
    console.log("Password length:", password?.length);
    console.log("Organization name:", organizationName);
    console.log("fbclid:", fbclid ? fbclid.substring(0, 20) + "..." : null);
    console.log("Timestamp:", new Date().toISOString());

    try {
      setIsLoading(true);

      // Build user metadata including fbclid if available
      const userMetadata: Record<string, any> = {
        organization_name: organizationName || "",
      };

      // Add fbclid to metadata if available (for Meta tracking)
      if (fbclid) {
        userMetadata.fbclid = fbclid;
        userMetadata.fbclid_timestamp = Date.now();
        console.log(
          "[Auth] Including fbclid in user metadata for Meta tracking",
        );
      }

      const signUpPayload = {
        email,
        password,
        options: {
          data: userMetadata,
        },
      };

      console.log("Sign up payload:", {
        email: signUpPayload.email,
        hasPassword: !!signUpPayload.password,
        passwordLength: signUpPayload.password?.length,
        options: signUpPayload.options,
      });

      const { data, error } = await supabase.auth.signUp(signUpPayload);

      console.log("Supabase response data:", data);
      console.log("Supabase response error:", error);

      if (error) {
        // Enhanced error logging
        console.error("❌ Sign up failed with detailed error:");
        console.log("Error object:", error);
        console.log("Error constructor:", error.constructor.name);
        console.log("Error prototype:", Object.getPrototypeOf(error));
        console.log("All error properties:", Object.getOwnPropertyNames(error));

        // Try to extract additional error information
        const errorDetails = {
          message: error.message,
          code: (error as any)?.code,
          status: (error as any)?.status,
          statusCode: (error as any)?.statusCode,
          statusText: (error as any)?.statusText,
          cause: (error as any)?.cause,
          details: (error as any)?.details,
          hint: (error as any)?.hint,
          rawError: error,
        };

        console.log("Extracted error details:", errorDetails);

        // Check if this is specifically a database error
        if (error.message?.includes("Database error saving new user")) {
          console.error(
            "🔍 This is the 'Database error saving new user' issue!",
          );
          console.log("Potential causes to investigate:");
          console.log("1. Database trigger failure");
          console.log("2. Database constraint violation");
          console.log("3. Database connection issue");
          console.log("4. Insufficient database permissions");
          console.log("5. Profile table schema mismatch");
          console.log("6. Organization creation failure");

          // Log browser and environment info
          console.log("🌐 Browser Environment:");
          console.log("- User Agent:", navigator.userAgent);
          console.log("- Online status:", navigator.onLine);
          console.log("- Current URL:", window.location.href);
          console.log(
            "- Local Storage available:",
            typeof Storage !== "undefined",
          );
        }

        // Log detailed error information for debugging
        logAuthError(error, "Sign Up");

        // Show user-friendly error message based on specific error code
        const errorInfo = formatAuthError(error);
        toast({
          variant: errorInfo.variant,
          title: errorInfo.title,
          description: errorInfo.description,
        });

        console.groupEnd();
        return { error };
      }

      console.log("✅ Sign up successful!");
      console.log("User created:", data.user);
      console.log("Session:", data.session);

      // Force session refresh to trigger AuthContext update immediately
      console.log("Forcing session refresh to update AuthContext...");

      // Wait a bit for Supabase's internal auth state to settle
      await new Promise((resolve) => setTimeout(resolve, 100));

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) {
        console.error("Error refreshing session:", sessionError);
      } else {
        console.log(
          "Session refreshed successfully:",
          sessionData.session?.user?.id,
        );
      }

      // Additional wait to ensure auth state change propagates to AuthContext
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Track sign up event in Segment
      if (data.user) {
        console.log("Tracking sign up event for user:", data.user.id);
        analytics.trackSignUp(data.user.id, {
          email: data.user.email,
          organization_name: organizationName,
          auth_provider: "email",
        });
      }

      console.groupEnd();
      // Note: Toast notification is handled by useRegisterForm to avoid duplicates
      return { error: null };
    } catch (error) {
      console.error("❌ Unexpected error during sign up:");
      console.log("Error object:", error);
      console.log("Error type:", typeof error);
      console.log("Error constructor:", error?.constructor?.name);

      toast({
        variant: "destructive",
        title: "Registration failed",
        description: "An unexpected error occurred. Please try again.",
      });

      console.groupEnd();
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);

      // Reset analytics before signing out
      analytics.reset();

      await supabase.auth.signOut();

      // Clean up any temporary session storage and event listeners
      sessionStorage.removeItem("auth-temporary-session");

      // Remove visibility change event listener if it exists
      cleanupVisibilityHandler();

      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Sign out failed",
        description: "An error occurred while signing out. Please try again.",
      });
      console.error("Sign out error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        // Log detailed error information for debugging
        logAuthError(error, "Password Reset");

        // Show user-friendly error message based on specific error code
        const errorInfo = formatAuthError(error);
        toast({
          variant: errorInfo.variant,
          title: errorInfo.title,
          description: errorInfo.description,
        });
        return { error };
      }

      toast({
        title: "Password reset email sent",
        description: "Check your email for a password reset link.",
      });

      return { error: null };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Password reset failed",
        description: "An unexpected error occurred. Please try again.",
      });
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Password update failed",
          description: error.message,
        });
        return { error };
      }

      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
      });

      return { error: null };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Password update failed",
        description: "An unexpected error occurred. Please try again.",
      });
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };
}
